import { create } from 'zustand'
import type { User } from '@portal/types'
import { authApi } from '@/lib/api/auth'

const ACCESS_TOKEN_KEY = 'portal_access_token'
const REFRESH_TOKEN_KEY = 'portal_refresh_token'

interface AuthState {
  user: User | null
  accessToken: string | null
  modules: string[]
  isAuthenticated: boolean
  isLoading: boolean
  setAuth: (user: User, accessToken: string, refreshToken: string, modules: string[]) => void
  setAuthPartial: (user: User, accessToken: string, modules: string[]) => void
  clearAuth: () => void
  initializeFromStorage: () => Promise<void>
  hasModule: (module: string) => boolean
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  modules: [],
  isAuthenticated: false,
  isLoading: true,

  setAuth: (user, accessToken, refreshToken, modules) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken)
    set({ user, accessToken, modules, isAuthenticated: true, isLoading: false })
  },

  setAuthPartial: (user, accessToken, modules) => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    set({ user, accessToken, modules, isAuthenticated: true, isLoading: false })
  },

  clearAuth: () => {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    set({ user: null, accessToken: null, modules: [], isAuthenticated: false, isLoading: false })
  },

  initializeFromStorage: async () => {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)
    if (!accessToken) {
      set({ isLoading: false })
      return
    }
    try {
      const response = await authApi.getMe()
      if (response.success && response.data) {
        set({
          user: response.data,
          accessToken,
          modules: response.data.modules ?? [],
          isAuthenticated: true,
          isLoading: false,
        })
      } else {
        localStorage.removeItem(ACCESS_TOKEN_KEY)
        localStorage.removeItem(REFRESH_TOKEN_KEY)
        set({ isLoading: false })
      }
    } catch {
      localStorage.removeItem(ACCESS_TOKEN_KEY)
      localStorage.removeItem(REFRESH_TOKEN_KEY)
      set({ isLoading: false })
    }
  },

  hasModule: (module: string) => get().modules.includes(module),
}))
